import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { careerLayout, careerType } from 'services/careerType';

import { breadcrumbsFor } from '../model/skillGraphNav';
import { ISkillGraphIndex } from '../model/skillGraphModel';

/**
 * Крошки карты (ТЗ 0070).
 *
 * Здесь живут ПРЕДКИ, которых нет на холсте. При раскладке слева направо фокус
 * — левый край, и колонки предков съели бы ширину, нужную содержимому. Убрать
 * их вовсе тоже нельзя: человек перестаёт понимать, чью карту смотрит.
 */
interface ISkillMapBreadcrumbs {
  index: ISkillGraphIndex;
  selected: string | null;
  /** Группа, до которой сужена карта. Крошки обязаны её показывать. */
  focus: string | null;
  /** Все профессии — корневая крошка ими же и переключается. */
  tracks: { code: string; title: string }[];
  /** Код показанной профессии: он же значение переключателя. */
  currentTrack: string;
  onGoTo: (key: string) => void;
  onChangeTrack: (code: string) => void;
}

export const SkillMapBreadcrumbs = memo(
  ({
    index,
    selected,
    focus,
    tracks,
    currentTrack,
    onGoTo,
    onChangeTrack,
  }: ISkillMapBreadcrumbs) => {
    const { t } = useTranslation();
    const trail = breadcrumbsFor(index, { focus, selected });

    return (
      <Nav aria-label={t('careerCenter.map.breadcrumbs')}>
        {trail.map((step, position) => {
          const last = position === trail.length - 1;

          /**
           * 🔴 Корневая крошка И ЕСТЬ переключатель профессии. Раньше рядом
           * стояли две вещи, называвшие одно и то же: крошка «DevOps engineer» и
           * селект «Профессия: DevOps engineer». Один смысл — один орган
           * управления, и заодно 156 px ширины обратно карте.
           */
          if (step.key === 'track' && tracks.length > 1) {
            return (
              <React.Fragment key={step.key}>
                <TrackPicker
                  value={currentTrack}
                  aria-label={t('careerCenter.map.profession')}
                  onChange={(event) => onChangeTrack(event.target.value)}>
                  {tracks.map((track) => (
                    <option key={track.code} value={track.code}>
                      {track.title}
                    </option>
                  ))}
                </TrackPicker>
                {!last && <Divider aria-hidden="true">›</Divider>}
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={step.key}>
              {position > 0 && <Divider aria-hidden="true">›</Divider>}
              {last ? (
                <Current aria-current="page" title={step.label}>
                  {step.label}
                </Current>
              ) : (
                <Step type="button" onClick={() => onGoTo(step.key)}>
                  {step.label}
                </Step>
              )}
            </React.Fragment>
          );
        })}
      </Nav>
    );
  },
);

const Nav = styled.nav`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  flex-wrap: nowrap;
`;

const Step = styled.button`
  ${careerType.meta()};
  flex: 0 0 auto;
  border: none;
  background: none;
  padding: 0 2px;
  min-height: ${careerLayout.hitTarget}px;
  color: ${Colors.gray_dark};
  cursor: pointer;
  white-space: nowrap;
  text-decoration: underline;
`;

/**
 * Последняя крошка — резинка ряда: она усекается многоточием, а полное имя
 * читается наведением. Усекается именно она, потому что предки короткие и
 * кликабельные, а текущее место человек и так видит на холсте.
 */
const Current = styled.span`
  ${careerType.metaStrong()};
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${Colors.black};
`;

const Divider = styled.span`
  ${careerType.meta()};
  flex: 0 0 auto;
  color: ${Colors.gray_dark};
`;

/** Тот же плотный вид, что у контролов панели: крошки стоят с ними в одном ряду. */
const TrackPicker = styled.select`
  ${careerType.metaStrong()};
  flex: 0 0 auto;
  max-width: 200px;
  height: ${careerLayout.hitTarget}px;
  padding: 0 6px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;
  color: ${Colors.black};
  background: ${Colors.white};
  cursor: pointer;
`;
